import { User, getAuth, signInWithEmailAndPassword } from "firebase/auth";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { firebaseApp } from "@/utils/firebase";

import { AuthContext } from "./context";

export function AuthContextProvider({ children }: { children: React.ReactNode }) {
    const [authUser, setAuthUser] = useState<User | null>(null);
    const [authUserLoading, setAuthUserLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = getAuth(firebaseApp).onAuthStateChanged(async user => {
            if (user) {
                setAuthUser(user);
            } else {
                setAuthUser(null);
            }
            setAuthUserLoading(false);
            unsubscribe();
        });

        return () => unsubscribe();
    }, []);

    const signIn = useCallback(async (email: string, password: string) => {
        // LOCAL DEV BYPASS - Remove this in production!
        if (
            process.env.NODE_ENV === "development" &&
            email === "asbjorn@fantasychess.app" &&
            password === "localdev"
        ) {
            // Mock the admin user for local development
            setAuthUser({
                uid: "q1wjimsABmd2nmdtOX28F43FAAB3",
                email: "asbjorn@fantasychess.app",
                emailVerified: true,
                displayName: "Local Admin",
                getIdToken: async () => "fake-token-for-local-dev",
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any);
            return;
        }

        await signInWithEmailAndPassword(getAuth(firebaseApp), email, password);
        setAuthUser(getAuth(firebaseApp).currentUser);
    }, []);

    const signOut = useCallback(async () => {
        await getAuth(firebaseApp).signOut();
    }, []);

    const refetchAuthUser = useCallback(async () => {
        await getAuth(firebaseApp).currentUser?.reload();
        setAuthUser(getAuth(firebaseApp).currentUser);
    }, []);

    useEffect(() => {
        const unsubscribe = getAuth(firebaseApp).onAuthStateChanged(async user => {
            if (user) {
                setAuthUser(user);
            } else {
                setAuthUser(null);
            }
            setAuthUserLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const value = useMemo(() => {
        return {
            authUser,
            authUserLoading,
            setAuthUser,
            refetchAuthUser,
            signIn,
            signOut,
        };
    }, [authUser, authUserLoading, refetchAuthUser, signIn, signOut]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
